import React from 'react';
import { Layout } from '../components/layout/Layout';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Button } from '../components/ui/Button';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Heart } from 'lucide-react';
export function AboutPage() {
  return <Layout data-id="element-273">
      {/* Header */}
      <div className="bg-[#0A1F44] pt-44 md:pt-48 pb-20 text-center text-white relative overflow-hidden" data-id="element-274">
        <div className="absolute inset-0 opacity-30" data-id="element-275">
          <img src="https://images.unsplash.com/photo-1507692049790-de58290a4334?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80" alt="Background" className="w-full h-full object-cover" data-id="element-276" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A1F44] via-[#0A1F44]/70 to-[#0A1F44]" data-id="element-277" />
        <div className="container-custom relative z-10" data-id="element-278">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="text-4xl md:text-6xl font-serif font-bold mb-6" data-id="element-279">
            About Us
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.15
        }} className="text-xl text-white/80 max-w-2xl mx-auto" data-id="element-280">
            Get to know our story, our vision, and our leadership.
          </motion.p>
        </div>
      </div>

      {/* Our Story */}
      <section className="py-20 bg-white" data-id="element-281">
        <div className="container-custom" data-id="element-282">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-283">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} data-id="element-284">
              <SectionHeading title="Our Story" subtitle="How It Started" alignment="left" data-id="element-285" />
              <div className="space-y-6 text-gray-600 leading-relaxed text-lg" data-id="element-286">
                <p data-id="element-287">
                  Household of Christ Church began with a small group of
                  believers gathering in a living room, united by a shared
                  passion for God's Word and a desire to see lives transformed
                  by His presence.
                </p>
                <p data-id="element-288">
                  Over the years, we've grown from that living room into a
                  vibrant, diverse community of faith. While our numbers have
                  increased, our core mission remains the same: to be a place
                  where people can encounter God, belong to a loving family, and
                  be equipped to fulfill their God-given purpose.
                </p>
                <p data-id="element-289">
                  Today, we continue to build on that foundation, reaching out
                  to our city and beyond with the life-changing message of Jesus
                  Christ.
                </p>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="relative" data-id="element-290">
              <img src="https://cdn.magicpatterns.com/uploads/qpPqkU7rtoXtdFFAqwmDsh/INSIDE_CHURCH_-_WEBSITE.png" alt="Church Community" className="rounded-2xl shadow-2xl w-full object-cover h-[500px]" data-id="element-291" />
              <div className="absolute -bottom-6 -left-6 bg-[#fc0719] p-6 rounded-lg shadow-lg max-w-xs hidden md:block" data-id="element-292">
                <p className="text-white font-serif italic text-lg" data-id="element-293">
                  "A Christ-Centered Family where Heaven touches Earth"
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-20 bg-[#F5F7FA]" data-id="element-294">
        <div className="container-custom" data-id="element-295">
          <SectionHeading title="Mission & Values" subtitle="What Drives Us" data-id="element-296" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12" data-id="element-297">
            {[{
            year: '2008',
            title: 'Our Beginning',
            description: "TheHousehold was founded with a divine vision to be a gateway between heaven and earth. A place where people encounter God's presence, experience transformation, and discover their purpose.",
            color: 'bg-[#fc0719]'
          }, {
            title: 'Love People',
            desc: 'We are called to love our neighbors as ourselves and serve our community.',
            color: 'bg-[#fc0719]'
          }, {
            title: 'Make Disciples',
            desc: 'We are committed to sharing the gospel and helping others grow in their faith.',
            color: 'bg-[#163A70]'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-white p-8 rounded-xl shadow-md text-center border-t-4 border-[#fc0719]" data-id="element-298">
                <div className={`w-16 h-16 ${item.color} rounded-full mx-auto mb-6 flex items-center justify-center text-white font-bold text-xl`} data-id="element-299">
                  {i + 1}
                </div>
                <h3 className="text-2xl font-serif font-bold text-[#0A1F44] mb-4" data-id="element-300">
                  {item.title}
                </h3>
                <p className="text-gray-600" data-id="element-301">{item.desc || item.description}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="py-20 bg-white" data-id="element-302">
        <div className="container-custom" data-id="element-303">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-304">
            {/* Left: Pastors Photo */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="relative" data-id="element-305">
              <img src="https://cdn.magicpatterns.com/uploads/r6gjE26mYY4Q1TvwRXfmTm/WhatsApp_Image_2026-03-17_at_8.04.04_PM.jpg" alt="Pastor Chris & Stella" className="w-full rounded-2xl shadow-xl" data-id="element-306" />
            </motion.div>

            {/* Right: Content */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-307">
              <SectionHeading title="Our Pastors" subtitle="Leadership" alignment="left" data-id="element-308" />

              <div className="space-y-3 mb-6" data-id="element-309">
                <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#0A1F44]/60" data-id="element-310">
                  Meet Our Pastors
                </span>
                <h3 className="text-2xl md:text-3xl font-serif font-bold text-[#0A1F44]" data-id="element-311">
                  Pastor Chris & Stella Uyanwune
                </h3>
                <p className="text-lg font-medium text-[#fc0719] italic" data-id="element-312">
                  Leading with Love & Vision
                </p>
              </div>

              <div className="space-y-6 text-gray-600 leading-relaxed" data-id="element-313">
                <p data-id="element-314">
                  Our pastoral team leads TheHousehold with a heart for God and
                  a deep love for His people. With years of ministry experience
                  and a commitment to biblical truth, they serve our community
                  with wisdom and grace.
                </p>
                <p data-id="element-315">
                  Their vision for TheHousehold is simple yet profound: to see
                  every person experience the life-transforming power of Jesus
                  Christ and walk in their God-given purpose.
                </p>
              </div>

              {/* Quote */}
              <div className="border-l-4 border-[#fc0719] pl-6 mb-8 mt-12" data-id="element-316">
                <h3 className="text-2xl font-serif font-bold text-[#0A1F44] mb-4" data-id="element-317">
                  Join Our Family
                </h3>
                <p className="text-gray-600 text-lg" data-id="element-318">
                  Whether you're just starting your spiritual journey or looking
                  for a place to call home, there's a seat for you at
                  TheHousehold.
                </p>
              </div>

              <div className="flex flex-wrap gap-4" data-id="element-319">
                <Link to="/visit" data-id="element-320">
                  <button className="inline-flex items-center gap-2 bg-[#fc0719] hover:bg-[#d4061a] text-white font-bold tracking-wider px-8 py-4 rounded-lg transition-colors duration-300 shadow-lg shadow-[#fc0719]/20" data-id="element-321">
                    PLAN YOUR VISIT
                    <ArrowRight className="w-5 h-5" data-id="element-322" />
                  </button>
                </Link>
                <Link to="/contact" data-id="element-323">
                  <Button variant="outline" size="lg" className="bg-[#fc0719] text-white hover:bg-[#d4061a] border-none" data-id="element-324">
                    CONTACT US
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Join Us CTA */}
      <section className="py-20 bg-[#0A1F44]" data-id="element-325">
        <div className="container-custom text-center" data-id="element-326">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0A1F44] mb-6" data-id="element-327">
            Come Visit Us
          </h2>
          <p className="text-lg text-gray-200 mb-10" data-id="element-328">
            We'd love to welcome you to TheHousehold. Join us for a service and
            experience the presence of God and the warmth of our community.
          </p>
          <Link to="/visit" data-id="element-329">
            <Button variant="secondary" size="lg" className="bg-[#fc0719] text-white hover:bg-[#d4061a] border-none" data-id="element-330">
              Plan Your Visit
            </Button>
          </Link>
        </div>
      </section>
    </Layout>;
}